import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { FetchDataArg } from '../utils/fetch';

export const fetchCustomerData = createAsyncThunk(
  'customer/fetchData',
  async (arg: FetchDataArg) => {
    return await arg.fetch<{ data: SiteEntity }>(arg.endpoint, arg.options);
  }
);

const initialCustomerState: SliceInitialState<{ data: SiteEntity }> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const customerSlice = createSlice({
  name: 'customer',
  initialState: initialCustomerState,
  reducers: {
    resetState: () => initialCustomerState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchCustomerData.fulfilled,
        (
          state,
          action: {
            payload:
              | GridusResponseType<{ data: SiteEntity }>
              | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as GridusResponseType<{
              data: SiteEntity;
            }>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchCustomerData.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const fetchMeterDailyData = createAsyncThunk(
  'meter/daily/fetchData',
  async (arg: FetchDataArg) => {
    return await arg.fetch<{ data: MeterDailyEntity }>(
      arg.endpoint,
      arg.options
    );
  }
);

const initialMeterDailyState: SliceInitialState<{ data: MeterDailyEntity }> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const meterDailySlice = createSlice({
  name: 'meter',
  initialState: initialMeterDailyState,
  reducers: {
    resetState: () => initialMeterDailyState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeterDailyData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchMeterDailyData.fulfilled,
        (
          state,
          action: {
            payload:
              | GridusResponseType<{ data: MeterDailyEntity }>
              | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as GridusResponseType<{
              data: MeterDailyEntity;
            }>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchMeterDailyData.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const fetchMeterMonthlyData = createAsyncThunk(
  'meter/monthly/fetchData',
  async (arg: FetchDataArg) => {
    return await arg.fetch<{ data: MeterMonthlyEntity }>(
      arg.endpoint,
      arg.options
    );
  }
);

const initialMeterMonthlyState: SliceInitialState<{
  data: MeterMonthlyEntity;
}> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const meterMonthlySlice = createSlice({
  name: 'meter',
  initialState: initialMeterMonthlyState,
  reducers: {
    resetState: () => initialMeterMonthlyState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeterMonthlyData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchMeterMonthlyData.fulfilled,
        (
          state,
          action: {
            payload:
              | GridusResponseType<{ data: MeterMonthlyEntity }>
              | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as GridusResponseType<{
              data: MeterMonthlyEntity;
            }>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchMeterMonthlyData.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const fetchSiteName = createAsyncThunk(
  'customer/site/name',
  async (arg: FetchDataArg) => {
    return await arg.fetch<{
      data: Array<{ siteId?: string; siteName?: string }>;
    }>(arg.endpoint, arg.options);
  }
);

const initialSiteNameState: SliceInitialState<{
  data: Array<{ siteId?: string; siteName?: string }>;
}> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const siteNameSlice = createSlice({
  name: 'siteName',
  initialState: initialSiteNameState,
  reducers: {
    resetState: () => initialSiteNameState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteName.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchSiteName.fulfilled,
        (
          state,
          action: {
            payload:
              | GridusResponseType<{
                  data: Array<{ siteId?: string; siteName?: string }>;
                }>
              | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as GridusResponseType<{
              data: Array<{ siteId?: string; siteName?: string }>;
            }>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchSiteName.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const { resetState: resetCustomerState } = customerSlice.actions;
export const { resetState: resetMeterDailyState } = meterDailySlice.actions;
export const { resetState: resetMeterMonthlyState } = meterMonthlySlice.actions;
export const { resetState: resetSiteNameState } = siteNameSlice.actions;
const { reducer: customerReducer } = customerSlice;
const { reducer: meterDailyReducer } = meterDailySlice;
const { reducer: meterMonthlyReducer } = meterMonthlySlice;
const { reducer: siteNameReducer } = siteNameSlice;
export {
  customerReducer,
  meterDailyReducer,
  meterMonthlyReducer,
  siteNameReducer,
};
